import React, { useEffect, useRef } from "react";
import { useGLTF, useCubeTexture, useTexture } from "@react-three/drei";
import {
  MeshPhysicalMaterial,
  DoubleSide,
  MeshBasicMaterial,
  Vector3,
  LinearFilter,
} from "three";
import { useFrame, useThree } from "@react-three/fiber";

import {
  EffectComposer,
  Vignette,
  SelectiveBloom,
  SMAA,
} from "@react-three/postprocessing";
import { Resizer, KernelSize } from "postprocessing";
import ieVersion from "ie-version";

const positionSet = {
  2: [
    {
      position: [-4.111, -0.491, 2],
      rotation: [0, (-80 / 180) * Math.PI, 0],
      scale: [0.9, 0.9, 0.9],
    },

    {
      position: [4.3683, -0.7, 2],
      rotation: [0, (-105 / 180) * Math.PI, 0],
      scale: [0.9, 0.9, 0.9],
    },
  ],
  3: [
    {
      position: [-9.75, 0, 2.94],
      rotation: [0, (-99 * Math.PI) / 180, 0],
      scale: [1, 1, 1],
    },
    {
      position: [0, -0.15, -0.06],
      rotation: [0, -Math.PI / 2, 0],
      scale: [1, 1, 1],
    },

    {
      position: [10.85, -0.2, 0.79],
      rotation: [0, -1.33, 0],
      scale: [1, 1, 1],
    },
  ],
  4: [
    {
      position: [-9.75, 0, 2.94],
      rotation: [0, (-99 * Math.PI) / 180, 0],
      scale: [0.9, 0.9, 0.9],
    },
    {
      position: [-6.111, -0.491, -0.3],
      rotation: [0, (-80 / 180) * Math.PI, 0],
      scale: [0.9, 0.9, 0.9],
    },

    {
      position: [6.3683, 2.7, 0.5275],
      rotation: [0, (-105 / 180) * Math.PI, 0],
      scale: [0.9, 0.9, 0.9],
    },

    {
      position: [10.85, -0.2, 0.79],
      rotation: [0, -1.33, 0],
      scale: [0.9, 0.9, 0.9],
    },
  ],

  5: [
    {
      position: [-9.75, 0, 2.94],
      rotation: [0, (-99 * Math.PI) / 180, 0],
      scale: [0.9, 0.9, 0.9],
    },
    {
      position: [-6.111, -0.491, -0.3],
      rotation: [0, (-80 / 180) * Math.PI, 0],
      scale: [0.9, 0.9, 0.9],
    },

    {
      position: [0, 0.1981, 0.06],
      rotation: [0, (-90 / 180) * Math.PI, 0],
      scale: [0.9, 0.9, 0.9],
    },

    {
      position: [6.3683, 2.7, 0.5275],
      rotation: [0, (-105 / 180) * Math.PI, 0],
      scale: [0.9, 0.9, 0.9],
    },

    {
      position: [10.85, -0.2, 0.79],
      rotation: [0, -1.33, 0],
      scale: [0.9, 0.9, 0.9],
    },
  ],
  6: [
    {
      position: [-9.75, 0, 2.94],
      rotation: [0, (-99 * Math.PI) / 180, 0],
      scale: [0.9, 0.9, 0.9],
    },
    {
      position: [-6.111, -0.491, -0.3],
      rotation: [0, (-80 / 180) * Math.PI, 0],
      scale: [0.9, 0.9, 0.9],
    },

    {
      position: [-2.3486, 6.431, -4.1847],
      rotation: [0, (-60 / 180) * Math.PI, 0],
      scale: [0.9, 0.9, 0.9],
    },

    {
      position: [0, 0.1981, 0.06],
      rotation: [0, (-90 / 180) * Math.PI, 0],
      scale: [0.9, 0.9, 0.9],
    },

    {
      position: [6.3683, 2.7, 0.5275],
      rotation: [0, (-105 / 180) * Math.PI, 0],
      scale: [0.9, 0.9, 0.9],
    },

    {
      position: [10.85, -0.2, 0.79],
      rotation: [0, -1.33, 0],
      scale: [0.9, 0.9, 0.9],
    },
  ],
  7: [
    {
      position: [-9.75, 0, 2.94],
      rotation: [0, (-99 * Math.PI) / 180, 0],
      scale: [0.9, 0.9, 0.9],
    },
    {
      position: [-6.111, -0.491, -0.3],
      rotation: [0, (-80 / 180) * Math.PI, 0],
      scale: [0.9, 0.9, 0.9],
    },

    {
      position: [-2.3486, 6.431, -4.1847],
      rotation: [0, (-60 / 180) * Math.PI, 0],
      scale: [0.9, 0.9, 0.9],
    },

    {
      position: [0, 0.1981, 0.06],
      rotation: [0, (-90 / 180) * Math.PI, 0],
      scale: [0.9, 0.9, 0.9],
    },
    {
      position: [4.0354, -1.885, 2.39],
      rotation: [0, (-124 / 180) * Math.PI, 0],
      scale: [0.9, 0.9, 0.9],
    },

    {
      position: [6.3683, 2.7, 0.5275],
      rotation: [0, (-105 / 180) * Math.PI, 0],
      scale: [0.9, 0.9, 0.9],
    },

    {
      position: [10.85, -0.5, 0.79],
      rotation: [0, -1.33, 0],
      scale: [0.9, 0.9, 0.9],
    },
  ],
};

export default function Model({
  products,
  setFocus,
  focusProduct,
  focusUrl,
  setFocusProduct,
  updateAnnotationPos,
  bloomLightRef1,
  bloomLightRef2,
  bloomLightRef3,
  onLoad,
  ...props
}) {
  const { gl } = useThree();
  const group = useRef();
  const { nodes } = useGLTF("./3dshowcase/gltf/uob_billboard.glb");
  const envMap = useCubeTexture(
    ["px.png", "nx.png", "py.png", "ny.png", "pz.png", "nz.png"],
    { path: "./3dshowcase/cubemap/" }
  );
  const meshRef1 = useRef();
  const meshRef2 = useRef();
  const meshRef3 = useRef();
  const meshRef4 = useRef();
  const meshRef5 = useRef();
  const meshRef6 = useRef();
  const meshRef7 = useRef();
  const hoverTimeoutRef = useRef();

  const billboardTextures = useTexture(products.map((p) => p.image));

  const m1 = new MeshPhysicalMaterial({
    color: "#030320",
    roughness: 0.9,
    metalness: 0.55,
    reflectivity: 1,
  });

  const m2 = new MeshPhysicalMaterial({
    color: "#002",
    roughness: 0.9,
    metalness: 0.8,
    reflectivity: 0.5,
    envMap: envMap,
  });

  const glass = new MeshPhysicalMaterial({
    color: "#000",
    reflectivity: 1,
    roughness: 0,
    envMap: envMap,
  });

  const redNeon = new MeshPhysicalMaterial({
    color: "#ff0404",
    emissive: 1,
    reflectivity: 0,
    roughness: 0,
  });
  const blueNeon = new MeshPhysicalMaterial({
    color: "#07f",
    emissive: 1,
    reflectivity: 0,
    roughness: 1,
  });

  useFrame(({ camera }) => {
    updateAnnotationPos(
      positionSet[products.length].map((p) => {
        const [x, y, z] = p.position;
        const _p = new Vector3(x, y, z).project(camera);
        return _p;
      })
    );
  });

  useEffect(() => {
    onLoad();
  }, []);

  let lastTap = useRef([0, 0]);

  return (
    <>
      <group ref={group} {...props} dispose={null}>
        <mesh
          castShadow
          receiveShadow
          geometry={nodes.floor.geometry}
          material={m1}
          position={[0.06, 0.01, -6.71]}
        />
        <group position={[1.3, 0.98, -5.89]}>
          <mesh
            ref={meshRef2}
            castShadow
            receiveShadow
            geometry={nodes.Cylinder002.geometry}
            material={m1}
          />
          <mesh
            ref={meshRef1}
            castShadow
            receiveShadow
            geometry={nodes.Cylinder002_1.geometry}
            material={glass}
          />
        </group>

        {positionSet[products.length].map((p, i) => {
          const billboardMaterial = new MeshBasicMaterial({
            reflectivity: 0,
            map: billboardTextures[i],
            side: DoubleSide,
          });
          billboardMaterial.map.minFilter = LinearFilter;
          billboardMaterial.map.maxFilter = LinearFilter;
          billboardMaterial.map.anisotropy = gl.capabilities.getMaxAnisotropy();

          return (
            <group {...p} key={p.position.join("_")}>
              <mesh
                castShadow
                receiveShadow
                geometry={nodes.BezierCurve011.geometry}
                material={m2}
              />
              <mesh
                castShadow
                receiveShadow
                geometry={nodes.BezierCurve011_1.geometry}
                material={billboardMaterial}
                onPointerEnter={() => {
                  document.body.style.cursor = "pointer";
                  if (hoverTimeoutRef.current) {
                    window.clearTimeout(hoverTimeoutRef.current);
                  }
                  hoverTimeoutRef.current = window.setTimeout(() => {
                    setFocus(p);
                    setFocusProduct(products[i]);
                  }, 1000);
                }}
                onPointerLeave={() => {
                  document.body.style.cursor = "auto";
                }}
                onPointerDown={(e) => {
                  lastTap.current = {
                    x: e.clientX,
                    y: e.clientY,
                  };
                }}
                onPointerUp={(e) => {
                  const thisTap = {
                    x: e.clientX,
                    y: e.clientY,
                  };

                  const dist = Math.sqrt(
                    Math.pow(Math.abs(lastTap.current.x - thisTap.x), 2) +
                      Math.pow(Math.abs(lastTap.current.y - thisTap.y), 2)
                  );

                  lastTap.current = { ...thisTap };

                  if (isNaN(dist) || dist > 10) {
                    return false;
                  } else {
                    if (focusProduct === i) {
                      window.location.href = focusUrl;
                    } else {
                      setFocus(p);
                      setFocusProduct(products[i]);
                    }
                  }
                }}
              />
            </group>
          );
        })}

        <mesh
          castShadow
          receiveShadow
          geometry={nodes.frame002.geometry}
          material={redNeon}
          position={[13.69, 3.89, -6.33]}
          rotation={[Math.PI / 2, 0, 1.14]}
          scale={5.87}
          ref={meshRef3}
        />
        <mesh
          castShadow
          receiveShadow
          geometry={nodes.frame.geometry}
          material={redNeon}
          position={[-9.75, 5.8, 3.73]}
          rotation={[Math.PI / 2, 0, -0.49]}
          scale={9.91}
          ref={meshRef4}
        />
        <mesh
          castShadow
          receiveShadow
          geometry={nodes.frame001.geometry}
          material={blueNeon}
          position={[6.31, 10, -5.84]}
          rotation={[Math.PI / 2, 0, 0.06]}
          scale={[6.29 * 1.5, 6.29 * 1.5, 7.45 * 1.5]}
          ref={meshRef5}
        />
        <mesh
          castShadow
          receiveShadow
          geometry={nodes.Text.geometry}
          material={redNeon}
          position={[-9.58, 8, 2.8]}
          rotation={[Math.PI / 2, 0, -0.49]}
          scale={[0.8, 0.8, 0.8]}
          ref={meshRef6}
        />
        <mesh
          castShadow
          receiveShadow
          geometry={nodes.Text001.geometry}
          material={blueNeon}
          position={[5.31, 14.15, -5.84]}
          rotation={[Math.PI / 2, 0, 0.06]}
          scale={[1.5, 1.5, 1.5]}
          ref={meshRef7}
        />
      </group>
      {!ieVersion.version && (
        <EffectComposer>
          <SMAA />
          <SelectiveBloom
            lights={[bloomLightRef1, bloomLightRef2, bloomLightRef3]}
            selection={[
              meshRef1,
              meshRef3,
              meshRef4,
              meshRef5,
              meshRef6,
              meshRef7,
            ]}
            intensity={1.1} // The bloom intensity.
            width={Resizer.AUTO_SIZE} // render width
            height={Resizer.AUTO_SIZE} // render height
            kernelSize={KernelSize.LARGE} // blur kernel size
            luminanceThreshold={0.01} // luminance threshold. Raise this value to mask out darker elements in the scene.
            luminanceSmoothing={0.015} // smoothness of the luminance threshold. Range is [0, 1]
          />

          <Vignette offset={0.2} darkness={0.4} />
        </EffectComposer>
      )}
    </>
  );
}

useGLTF.preload("./3dshowcase/gltf/uob_billboard.glb");
